import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { theme } from '@garnish/constants';
import {
  Button,
  DisplayText,
  IllusEmpty_2,
  LoadingPlaceholder,
  webOnlyStyles,
  useToggleState,
} from '@garnish/core';
import { Image } from '@garnish/media';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { getEnvVars } from '@order/utils';

import {
  useCustomerSheetPaymentElementCredentials,
  useNotifyStripeError,
  useSetDefaultPaymentMethod,
  useStripeLoadMonitor,
} from '../../hooks';
import { loadStripeWithRetry } from '../../utils';
import { usePaymentElementChangeListener, usePaymentElementSubmission } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const StripeCustomerSheet = (props: StripeCustomerSheetProps) => {
  const { onClose } = props;
  const { value: isReady, toggleOn: setIsReady } = useToggleState();

  const {
    clientSecret,
    customerSessionClientSecret,
    isFetching: isFetchingCustomerSession,
    hasError: hasCustomerSessionError,
  } = useCustomerSheetPaymentElementCredentials();

  const { value: hasFailedToLoadStripe, toggleOn: setHasFailedToLoadStripe } = useToggleState();

  const stripePromise = useMemo(() => loadStripeWithRetry(publicKey, 'account'), []);
  useStripeLoadMonitor(stripePromise, setHasFailedToLoadStripe);

  return (
    <View>
      {!isReady && !hasCustomerSessionError && !hasFailedToLoadStripe ? (
        <LoadingPlaceholder rows={4} rowHeight={[48, 48, 48, 48]} palette="darkGray" />
      ) : null}

      {hasCustomerSessionError || hasFailedToLoadStripe ? (
        <View style={styles.container}>
          <Image source={IllusEmpty_2} style={styles.illustration} />

          <DisplayText sizeMatch={['24']} style={styles.title}>
            {hasFailedToLoadStripe ? (
              <FormattedMessage {...messages.errorLoadingStripe} />
            ) : (
              <FormattedMessage {...messages.error} />
            )}
          </DisplayText>
        </View>
      ) : null}

      {isFetchingCustomerSession || hasCustomerSessionError ? null : (
        <Elements stripe={stripePromise} options={{ clientSecret, customerSessionClientSecret }}>
          <View style={isReady ? null : styles.hiddenContainer}>
            <LoadedForm onClose={onClose} setIsReady={setIsReady} />
          </View>
        </Elements>
      )}
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const LoadedForm = (props: LoadedFormProps) => {
  const { onClose, setIsReady } = props;

  const stripe = useStripe();
  const elements = useElements();

  const setDefaultPaymentMethod = useSetDefaultPaymentMethod();

  const notifyStripeError = useNotifyStripeError();

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { isSubmitting, handleSubmit } = usePaymentElementSubmission({
    stripe,
    elements,
    setDefaultPaymentMethod,
    notifyStripeError,
    onClose,
  });

  // ─── Effects ───────────────────────────────────────────────────────────────

  usePaymentElementChangeListener({
    stripe,
    elements,
    setDefaultPaymentMethod,
  });

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.container, maxHeight]}>
      <ScrollView style={maxHeight} contentContainerStyle={styles.scrollView}>
        <PaymentElement onReady={setIsReady} options={{ wallets }} />
      </ScrollView>

      <Button palette="primary" size="large-wide" isLoading={isSubmitting} onPress={handleSubmit}>
        <FormattedMessage {...messages.save} />
      </Button>
    </View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const wallets = {
  googlePay: 'never' as const,
  applePay: 'never' as const,
  link: 'never' as const,
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Stripe Customer Sheet | Error title',
  },
  errorLoadingStripe: {
    defaultMessage:
      'Something went wrong while loading the payment provider. Please refresh the page.',
    description: 'Stripe Customer Sheet | Error title',
  },
  save: {
    defaultMessage: 'Save card',
    description: 'Stripe Customer Sheet | Save CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const maxHeight = webOnlyStyles({ maxHeight: '75svh' as never });
const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: theme.spacing['6'],
    paddingBottom: theme.spacing['4'],
  },
  scrollView: {
    flexGrow: 1,
  },
  illustration: {
    width: 150,
    height: 150,
    alignSelf: 'center',
  },
  title: {
    textAlign: 'center',
  },
  hiddenContainer: {
    display: 'none',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type StripeCustomerSheetProps = {
  visible: boolean;
  onClose: () => void;
};

type LoadedFormProps = {
  onClose: () => void;
  setIsReady: () => void;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const publicKey = getEnvVars().STRIPE_PUBLIC_KEY;
